/*
 * TruCustom Customizer — storefront runtime.
 * Design notes and rationale: SHOPIFY_APP_INTEGRATION_PLAN.md (Phase 2).
 *
 * Two rules shape this file:
 *   1. Degrade to nothing. Any failure must leave the page exactly as it would be
 *      without the app. A stuck-disabled add-to-cart is a revenue outage.
 *   2. Never decide the property contract locally — the server does. Extension
 *      releases are immutable and hit every installed store at once.
 */
(function () {
  'use strict';

  // Bail if another copy of this file already ran. TruCustom can reach a
  // storefront two ways — as the Shopify app's theme extension, or as theme code
  // pasted in while app review is pending — and a store mid-migration can have
  // both. Two copies would decorate the same cart lines twice.
  if (window.__TRUCUSTOM_CUSTOMIZER__) return;
  window.__TRUCUSTOM_CUSTOMIZER__ = true;

  var PREFIX = '[TruCustom]';
  var TOKEN_STORE_PREFIX = 'trucustom:design:';

  function warn(message, error) {
    try { console.warn(PREFIX + ' ' + message, error === undefined ? '' : error); } catch (e) {}
  }

  function originOf(url) {
    try { return new URL(url, window.location.href).origin; } catch (e) { return null; }
  }

  function trimSlash(value) {
    return String(value || '').replace(/\/+$/, '');
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  function boot() {
    var roots = document.querySelectorAll('[data-trucustom-root]');
    if (!roots.length) return;

    if (roots.length > 1) {
      warn('Found ' + roots.length + ' blocks; using the first. Enable the app block OR the embed, not both.');
    }

    try {
      start(buildContext(roots[0]));
    } catch (error) {
      warn('Failed to start; leaving the page untouched.', error);
    }
  }

  function buildContext(root) {
    var data = root.dataset;
    var baseUrl = trimSlash(data.baseUrl);

    return {
      root: root,
      baseUrl: baseUrl,
      baseOrigin: originOf(baseUrl),
      storeId: (data.storeId || '').trim(),
      // Identifies the store when no TruCustom UUID is available. Used when
      // TruCustom ships as theme code: one identical snippet on every store,
      // with {{ shop.permanent_domain }} as the only store identity Liquid has.
      // The lookup returns the UUID, which embedUrl() then needs.
      shopDomain: (data.shopDomain || '').trim(),
      shopifyProductId: (data.productId || '').trim(),
      variantId: (data.variantId || '').trim(),
      // A single print/decoration method the THEME already knows for this
      // product — typically sourced from a Shopify metafield the store uses
      // for its own production tracking, independent of TruCustom's own
      // print-type config. Takes priority there: see the widget's
      // ProductCustomizer, which shows this as a fixed note instead of
      // product.print_types when set, and never records a print_type_id for
      // it (there is no corresponding PrintingMethod row — the value's source
      // of truth is the Shopify metafield, not TruCustom).
      forcedPrintType: (data.forcedPrintType || '').trim(),
      launch: data.launch === 'inline' ? 'inline' : 'modal',
      buttonLabel: data.buttonLabel || 'Customize',
      editLabel: data.editLabel || 'Edit design',
      removeLabel: data.removeLabel || 'Remove design',
      heading: data.heading || '',
      gateMessage: data.gateMessage || 'Finish customizing to add this to your cart.',
      // Shown when the merchant has marked this product as customize-only.
      requireMessage: data.requireMessage || data.gateMessage ||
        'Customize this product to add it to your cart.',
      // Shown when a customize-only product's dynamic checkout button (Buy it
      // now / Shop Pay / PayPal / etc) is clicked before a design exists — see
      // blockAcceleratedCheckout(). That button skips the add-to-cart form
      // entirely, so requireMessage's "add it to your cart" framing would be
      // wrong here.
      buyNowBlockedMessage: data.buyNowBlockedMessage ||
        'This product requires customization before you can check out.',
      // Set from the product lookup at boot — see the mount sequence.
      requireCustomization: false,
      showPreview: data.showPreview !== 'false',
      savedTitle: data.savedTitle || 'Your design is saved',
      savedNote: data.savedNote || 'Ready to add to your cart.',
      savingLabel: data.savingLabel || 'Saving your design…',
      takeover: data.takeover !== 'false',
      afterAdd: data.afterAdd || 'cart',
      frameHeight: parseInt(data.frameHeight, 10) || 700,
      floating: root.hasAttribute('data-trucustom-floating'),

      trucustomProductId: null,
      // Set from the product lookup at boot. Human-readable label only — used
      // on the surcharge cart line's "For" property so a merchant reading an
      // order can tell which product a print-type charge belongs to.
      productName: '',
      sessionToken: null,
      properties: null,
      // The Shopify variant id for the customer's selected print type's
      // surcharge, carried over from the widget's finalize payload — see
      // handleFinalize(). Null whenever there's nothing to charge: no print
      // types configured, a free one, or one not yet provisioned in Shopify.
      // Cleared alongside properties in clearProperties(), since a design
      // invalidated by a variant change may come back with a different print
      // type (and therefore a different surcharge) once redone.
      surchargeVariantId: null,
      forms: [],
      form: null,
      submitButtons: [],
      // Native add-to-cart buttons currently relabelled as the "Customize" CTA
      // — see swapButtonsToCustomize(). Separate from submitButtons, which the
      // transient save-round-trip disable in handleFinalize owns.
      requiredButtons: [],
      // The extra button render() creates in modal mode for an OPTIONAL
      // customizable product. Customize-only products don't get one — the
      // theme's own Add to cart stands in instead (see applyRequirement()).
      customizeTrigger: null,
      // { container, overlay, previousPosition } per dynamic-checkout button
      // currently covered by an overlay — see blockAcceleratedCheckout().
      paymentButtonBlockers: [],
      // The "customization required" popup shown when a covered checkout
      // button is clicked. Built once and reused (hidden/shown) like the
      // customizer's own modal.
      blockedDialog: null,
      notice: null,
      frame: null,
      modal: null,
      saved: null
    };
  }

  function start(ctx) {
    if (!ctx.baseUrl || !ctx.baseOrigin) return warn('No usable TruCustom URL configured.');
    if (!ctx.storeId && !ctx.shopDomain) return warn('No TruCustom store ID set on the block. Add it in the theme editor.');
    if (!ctx.shopifyProductId) return;

    lookupProduct(ctx)
      .then(function (product) {
        // Authoritative on/off, and it covers the store-wide kill switch too.
        // Not customizable is the normal case, not an error.
        if (!product || !product.customizable || !product.id) return;

        ctx.trucustomProductId = product.id;
        ctx.productName = product.name || '';
        ctx.forms = findProductForms(ctx.root, ctx.variantId);
        ctx.form = ctx.forms[0] || null;

        // A customizer whose output can't reach the cart would produce orders
        // nobody can fulfil. Show nothing instead.
        if (!ctx.form) return warn('No add-to-cart form found; not mounting.');

        ctx.requireCustomization = product.requireCustomization === true;

        ctx.sessionToken = readStoredToken(ctx);
        render(ctx);
        listenForMessages(ctx);
        watchVariantChanges(ctx);
        interceptAddToCart(ctx);
        interceptRequiredClicks(ctx);

        // Customizing is OPTIONAL by default, and gating on load blocked every
        // plain sale on every customizable product back in August. So the gate
        // applies here only when the merchant has explicitly marked this product
        // as customize-only.
        //
        // Gate first, then let restoreSavedDesign lift it: that call is async,
        // and starting open would give a customer a window in which they could
        // add a product to the cart that is not supposed to be sold blank.
        applyRequirement(ctx);
        restoreSavedDesign(ctx);
      })
      .catch(function (error) {
        warn('Product lookup failed; leaving the page untouched.', error);
        ungate(ctx);
      });
  }

  function lookupProduct(ctx) {
    var identifier = ctx.storeId
      ? 'store_id=' + encodeURIComponent(ctx.storeId)
      : 'shop_domain=' + encodeURIComponent(ctx.shopDomain);

    var url = ctx.baseUrl + '/api/v1/widget/products/lookup' +
      '?' + identifier +
      '&external_id=' + encodeURIComponent(ctx.shopifyProductId);

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (response.status === 404) return null; // not synced here — expected
        if (!response.ok) throw new Error('Lookup returned HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        if (!payload) return null;
        // Adopt the resolved UUID when we started from a domain: embedUrl()
        // builds /embed/<storeId>/… and has nothing else to put there.
        if (!ctx.storeId && payload.store && payload.store.id) ctx.storeId = payload.store.id;
        return payload.product;
      });
  }

  // ── The add-to-cart form ──────────────────────────────────────────────────

  // ALL of this product's cart forms, not one guess.
  //
  // Themes routinely render more than one /cart/add form for the same product —
  // a sticky mobile bar, duplicated buy buttons, a drawer quick-add. They post
  // the same variant and the customer picks which one at click time, so every
  // one of them has to carry the design. Decorating only the first form in the
  // document is a silent order-losing bug: the design saves, the page looks
  // right, and the line arrives with no properties because the button the
  // customer pressed belonged to the other form.
  //
  // Matching on the variant id is the guard that keeps this safe. A quick-add
  // card for a DIFFERENT product also has a /cart/add form and a [name="id"],
  // and writing this design onto that one would attach the customer's artwork
  // to the wrong product — worse than the bug being fixed.
  function findProductForms(root, variantId) {
    var all = document.querySelectorAll('form[action*="/cart/add"]');
    var matched = [];
    var withVariant = [];

    for (var i = 0; i < all.length; i++) {
      var input = all[i].querySelector('[name="id"]');
      if (!input) continue;
      withVariant.push(all[i]);
      if (variantId && String(input.value || '').trim() === variantId) matched.push(all[i]);
    }

    if (matched.length) {
      // The form enclosing the block goes first — the merchant positioned the
      // customizer against it, so it is the right anchor for the inline frame.
      var enclosing = root.closest && root.closest('form[action*="/cart/add"]');
      var index = enclosing ? matched.indexOf(enclosing) : -1;
      if (index > 0) {
        matched.splice(index, 1);
        matched.unshift(enclosing);
      }
      return matched;
    }

    // No variant match — a theme that renders the id differently, or a block
    // configured without one. Fall back to the old single-form behaviour rather
    // than decorating every form on the page.
    if (withVariant.length) return [withVariant[0]];
    return all[0] ? [all[0]] : [];
  }

  // A button the theme already disabled means sold out. Never touch those — in
  // either direction.
  function collectSubmitButtons(ctx) {
    var managed = [];

    ctx.forms.forEach(function (form) {
      var found = form.querySelectorAll('[type="submit"], [name="add"]');
      for (var i = 0; i < found.length; i++) {
        if (!found[i].disabled) managed.push(found[i]);
      }
    });
    return managed;
  }

  /**
   * Hold add-to-cart while a customize-only product has no design attached.
   *
   * Called wherever the design can come or go — boot, discard, and a variant
   * change that invalidates the design — so there is one rule rather than three
   * places remembering to re-gate.
   *
   * No-op for ordinary products, which is nearly all of them.
   *
   * In modal mode (the common case) the requirement is enforced by RELABELLING
   * the theme's own submit button(s) to ctx.buttonLabel (e.g. "Customize")
   * rather than disabling them and rendering a second button below: a disabled
   * button leaves a gap where Add to cart used to be and gives a customer who
   * clicks it no feedback at all, while a button that already says "Add to
   * cart" simply becoming "Customize" in place does not.
   *
   * Inline launch has no button to relabel (the customizer is already visible
   * on the page), so it falls back to the plain disable-in-place gate() used
   * elsewhere in this file.
   */
  function applyRequirement(ctx, options) {
    if (!ctx.requireCustomization) return;

    if (ctx.properties) {
      restoreNativeButtons(ctx);
      unblockAcceleratedCheckout(ctx);
      ungate(ctx); // releases the inline-mode fallback too; no-op otherwise
      return;
    }

    if (ctx.launch === 'inline') {
      gate(ctx);
    } else {
      swapButtonsToCustomize(ctx);
    }
    // Independent of launch mode: a dynamic checkout button skips the
    // customize/add-to-cart flow entirely regardless of how Customize itself
    // is triggered, so it needs covering either way.
    blockAcceleratedCheckout(ctx);

    // `silent` leaves a more specific message in place. On a variant change the
    // caller has already explained WHY the design went away, which tells the
    // customer more than "customize this product to add it to your cart" does.
    if (!options || !options.silent) showNotice(ctx, ctx.requireMessage);
  }

  // Relabels the theme's own submit button(s) to read ctx.buttonLabel (e.g.
  // "Customize") in place, leaving them enabled — interceptRequiredClicks() is
  // what stops a click from actually reaching the cart while they're in this
  // state. Distinct from gate()/ungate(), which remain a plain disable-in-place
  // used for the brief save round-trip in handleFinalize and as the
  // inline-launch fallback.
  //
  // Never touches a button the theme itself disabled (collectSubmitButtons
  // already skips those) — sold-out stays sold-out regardless of this feature.
  //
  // Re-collects and re-checks every button on every call rather than trusting
  // that a prior call still holds. Horizon rewrites a buy-buttons button's own
  // label on a variant change (to reflect that variant's own availability
  // wording), which silently undoes this relabel with no event of ours to
  // catch — the button itself stays the same node (so interceptRequiredClicks
  // still fires on it), but its text reverts to "Add to cart". Comparing each
  // button's current text against ctx.buttonLabel is what notices that and
  // re-applies it; an already-correct button is a no-op.
  function swapButtonsToCustomize(ctx) {
    var buttons = collectSubmitButtons(ctx);

    buttons.forEach(function (button) {
      if (button.getAttribute('data-trucustom-required') === 'true' &&
          button.textContent === ctx.buttonLabel) {
        return;
      }

      // textContent, not a stored label alone, because restoring it later has
      // to bring back whatever else lived in there — an icon span, a
      // sold-out/success-animation span, anything a theme nested inside its
      // own button — byte-for-byte, not just the visible words.
      button.setAttribute('data-trucustom-original-html', button.innerHTML);
      button.textContent = ctx.buttonLabel;
      button.setAttribute('data-trucustom-required', 'true');
    });

    ctx.requiredButtons = buttons;
  }

  function restoreNativeButtons(ctx) {
    (ctx.requiredButtons || []).forEach(function (button) {
      if (button.getAttribute('data-trucustom-required') !== 'true') return;
      var original = button.getAttribute('data-trucustom-original-html');
      if (original !== null) button.innerHTML = original;
      button.removeAttribute('data-trucustom-original-html');
      button.removeAttribute('data-trucustom-required');
    });
    ctx.requiredButtons = [];
  }

  // ── Dynamic checkout (Buy it now / Shop Pay / PayPal / etc) ───────────────
  //
  // `{{ form | payment_button }}` — the Liquid filter every theme uses for
  // this — always wraps its output in [data-shopify="payment-button"]; that
  // attribute is Shopify's own convention, not theme-specific, so it's safe to
  // rely on here in code shipped to many different stores/themes.
  //
  // This button skips the add-to-cart form (and therefore
  // swapButtonsToCustomize()/gate()) entirely — it builds its own checkout
  // request straight from the product/variant. Left alone, it's a complete
  // bypass of "require customization": a customer can check out with a blank
  // product no matter how the Add to cart side is guarded.
  //
  // It also can't be handled the way Add to cart is. Several of the payment
  // methods it can render (Shop Pay, PayPal, Google/Apple Pay) draw into a
  // cross-origin iframe Shopify controls — there is no click event on this
  // page for that content, so intercepting document/window clicks the way
  // interceptRequiredClicks() does for Add to cart would silently do nothing
  // for those. An overlay physically covering the container works regardless
  // of what's underneath, because the browser delivers the click to whatever
  // element is actually on top — the iframe never sees it.
  function findPaymentButtonContainers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-shopify="payment-button"]'));
  }

  // Re-verifies each tracked overlay is still actually attached, rather than
  // trusting that a non-empty ctx.paymentButtonBlockers still means "blocked".
  // A dynamic checkout button commonly re-renders its own container on a
  // variant change (it has to reflect the new variant's price/eligibility),
  // which can remove our overlay along with whatever else it replaces while
  // the array here stays stale — exactly the same class of bug as
  // swapButtonsToCustomize, for the button this feature exists to cover.
  function blockAcceleratedCheckout(ctx) {
    var blockers = ctx.paymentButtonBlockers.filter(function (entry) {
      return document.contains(entry.container) &&
        document.contains(entry.overlay) &&
        entry.container.contains(entry.overlay);
    });

    findPaymentButtonContainers().forEach(function (container) {
      var alreadyBlocked = blockers.some(function (entry) { return entry.container === container; });
      if (alreadyBlocked) return;

      // The overlay is `position: absolute`, which needs a positioned
      // ancestor to size against. Only set one when the theme hasn't already
      // — these containers are static in every theme checked so far, and this
      // never overwrites a value the theme itself set on purpose.
      var previousPosition = container.style.position;
      if (!previousPosition && window.getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }

      var overlay = document.createElement('button');
      overlay.type = 'button'; // must never submit any surrounding form
      overlay.className = 'trucustom__checkout-overlay';
      overlay.setAttribute('aria-label', 'Customize this product before checking out');
      overlay.addEventListener('click', function (event) {
        event.preventDefault();
        showAcceleratedCheckoutNotice(ctx);
      });

      container.appendChild(overlay);
      blockers.push({ container: container, overlay: overlay, previousPosition: previousPosition });
    });

    ctx.paymentButtonBlockers = blockers;
  }

  function unblockAcceleratedCheckout(ctx) {
    ctx.paymentButtonBlockers.forEach(function (entry) {
      if (entry.overlay.parentNode) entry.overlay.parentNode.removeChild(entry.overlay);
      // '' restores whatever the theme's own stylesheet says rather than
      // hardcoding a guess at what it was before.
      entry.container.style.position = entry.previousPosition;
    });
    ctx.paymentButtonBlockers = [];
    closeAcceleratedCheckoutNotice(ctx);
  }

  // Built once and reused (hidden/shown), like openModal()'s own dialog.
  function showAcceleratedCheckoutNotice(ctx) {
    if (ctx.blockedDialog) {
      ctx.blockedDialog.hidden = false;
      return;
    }

    var overlay = document.createElement('div');
    overlay.className = 'trucustom__overlay';

    var dialog = document.createElement('div');
    dialog.className = 'trucustom__blocked-dialog';
    dialog.setAttribute('role', 'alertdialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', 'Customization required');

    var message = document.createElement('p');
    message.className = 'trucustom__blocked-dialog-message';
    message.textContent = ctx.buyNowBlockedMessage;
    dialog.appendChild(message);

    var actions = document.createElement('div');
    actions.className = 'trucustom__blocked-dialog-actions';

    // Goes straight into the customizer rather than just dismissing — the
    // customer already told us what they want to do (buy this) by pressing
    // the covered button, so the popup's job is to redirect them, not just
    // to stop them.
    var customizeBtn = document.createElement('button');
    customizeBtn.type = 'button';
    customizeBtn.className = 'trucustom__button';
    customizeBtn.textContent = ctx.buttonLabel;
    customizeBtn.addEventListener('click', function () {
      closeAcceleratedCheckoutNotice(ctx);
      openModal(ctx);
    });
    actions.appendChild(customizeBtn);

    var cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'trucustom__notice-action';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', function () { closeAcceleratedCheckoutNotice(ctx); });
    actions.appendChild(cancelBtn);

    dialog.appendChild(actions);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeAcceleratedCheckoutNotice(ctx);
    });

    ctx.blockedDialog = overlay;
  }

  function closeAcceleratedCheckoutNotice(ctx) {
    if (ctx.blockedDialog) {
      ctx.blockedDialog.remove();
      ctx.blockedDialog = null;
    }
  }

  // A click landing on a button swapButtonsToCustomize() relabelled must open
  // the customizer instead of submitting the form it sits in — and must never
  // also reach the theme's OWN click handling for that button, or a customer
  // sees the theme's "added to cart" animation play for a click that didn't
  // add anything.
  //
  // Registered once, on window, in the capture phase. Several modern themes
  // (Shopify Horizon among them) wire their real add-to-cart handling through
  // a listener on document rather than the button itself, so stopping the
  // event at the button would be too late — document's listener already ran.
  // window sits above document in the event's propagation path, so a capture
  // listener there runs before ANY listener on document, regardless of which
  // script registered first. Also guards the form's submit event for the rare
  // case something reaches it without a click on the button (e.g. Enter in a
  // quantity field).
  function interceptRequiredClicks(ctx) {
    function guard(event) {
      if (!ctx.requiredButtons || !ctx.requiredButtons.length) return;

      var isOurButton = event.type === 'click' && ctx.requiredButtons.indexOf(event.target) !== -1;
      var isOurForm = event.type === 'submit' && ctx.forms.indexOf(event.target) !== -1;
      if (!isOurButton && !isOurForm) return;

      event.preventDefault();
      event.stopPropagation();
      openModal(ctx);
    }

    window.addEventListener('click', guard, true);
    window.addEventListener('submit', guard, true);
  }

  /**
   * Hold the add-to-cart buttons. Says nothing — the caller owns the message,
   * because the same mechanical hold serves three different situations and
   * baking one sentence in here overwrote the more specific ones.
   *
   * Idempotent. Re-collecting on a second call would come back EMPTY, because
   * collectSubmitButtons deliberately skips disabled buttons (a disabled button
   * normally means sold out and must never be re-enabled by us) — including the
   * ones we disabled ourselves. That wiped the only record of which buttons to
   * release, and add-to-cart stayed dead even after the design was attached.
   */
  function gate(ctx) {
    if (ctx.properties) return;
    if (ctx.submitButtons && ctx.submitButtons.length) return;

    ctx.submitButtons = collectSubmitButtons(ctx);
    ctx.submitButtons.forEach(function (button) {
      button.disabled = true;
      button.setAttribute('data-trucustom-gated', 'true');
    });
  }

  function ungate(ctx) {
    (ctx.submitButtons || []).forEach(function (button) {
      if (button.getAttribute('data-trucustom-gated') === 'true') {
        button.disabled = false;
        button.removeAttribute('data-trucustom-gated');
      }
    });
    ctx.submitButtons = [];
    hideNotice(ctx);
  }

  // `action` is optional: { label, onClick }. A gate that offers no way past it
  // is a dead end, so the in-progress gate uses this to let a customer abandon
  // the design and buy the plain product instead.
  function showNotice(ctx, message, action) {
    if (!message) return;
    if (!ctx.notice) {
      ctx.notice = document.createElement('p');
      ctx.notice.className = 'trucustom__notice';
      ctx.notice.setAttribute('role', 'status');
      ctx.root.appendChild(ctx.notice);
    }

    ctx.notice.textContent = message;

    if (action && action.label) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'trucustom__notice-action';
      button.textContent = action.label;
      button.addEventListener('click', action.onClick);
      ctx.notice.appendChild(document.createTextNode(' '));
      ctx.notice.appendChild(button);
    }

    ctx.notice.hidden = false;
  }

  function hideNotice(ctx) {
    if (ctx.notice) ctx.notice.hidden = true;
  }

  // ── UI ────────────────────────────────────────────────────────────────────

  function render(ctx) {
    if (ctx.floating) anchorToForm(ctx);

    if (ctx.heading) {
      var heading = document.createElement('p');
      heading.className = 'trucustom__heading';
      heading.textContent = ctx.heading;
      ctx.root.appendChild(heading);
    }

    if (ctx.launch === 'inline') {
      ctx.root.appendChild(buildFrame(ctx, 'trucustom__frame'));
      return;
    }

    // A customize-only product doesn't get a second button — applyRequirement()
    // relabels the theme's own Add to cart in place instead. Rendering both
    // was the "disabled button next to a working one, with a gap where Add to
    // cart used to be" layout this replaces.
    if (ctx.requireCustomization) return;

    var button = document.createElement('button');
    button.type = 'button'; // must never submit the product form
    button.className = 'trucustom__button';
    button.textContent = ctx.buttonLabel;
    button.addEventListener('click', function () { openModal(ctx); });
    ctx.root.appendChild(button);
    ctx.customizeTrigger = button;
  }

  // The embed block renders at end of <body>, so it has to find its own place.
  function anchorToForm(ctx) {
    try {
      if (ctx.form && ctx.form.parentNode) ctx.form.parentNode.insertBefore(ctx.root, ctx.form);
    } catch (error) {
      warn('Could not position the fallback embed.', error);
    }
  }

  function embedUrl(ctx) {
    var url = ctx.baseUrl + '/embed/' + encodeURIComponent(ctx.storeId) +
      '/' + encodeURIComponent(ctx.trucustomProductId);
    var params = [];

    if (ctx.variantId) params.push('variant_id=' + encodeURIComponent(ctx.variantId));
    if (ctx.sessionToken) params.push('session_token=' + encodeURIComponent(ctx.sessionToken));
    if (ctx.forcedPrintType) params.push('forced_print_type=' + encodeURIComponent(ctx.forcedPrintType));

    // Only meaningful with no token: it tells the customizer to ignore the resume
    // pointer it keeps in its OWN localStorage. That storage is on the TruCustom
    // origin, so this page can't clear it directly — dropping the token here is
    // not enough on its own, and without this the customizer reopens onto the
    // design the customer just removed.
    if (!ctx.sessionToken && ctx.forceFresh) params.push('fresh=1');

    return params.length ? url + '?' + params.join('&') : url;
  }

  // The one place the customizer frame is navigated, so what it is SHOWING and
  // what it SHOULD be showing can't silently diverge.
  //
  // embedUrl() carries the session token, so the frame's URL encodes which design
  // is open. Anything that changes ctx.sessionToken — discarding a design, a
  // failed restore — has to be followed by this, or the frame keeps editing a
  // design the page has already let go of.
  function syncFrame(ctx) {
    if (!ctx.frame) return;
    var wanted = embedUrl(ctx);
    if (ctx.frameUrl === wanted) return; // already there; reloading would discard canvas state
    ctx.frameUrl = wanted;
    ctx.frame.src = wanted;
  }

  function buildFrame(ctx, className) {
    var frame = document.createElement('iframe');
    frame.className = className;
    frame.setAttribute('title', 'Product customizer');
    frame.setAttribute('allow', 'clipboard-write');
    ctx.frameUrl = embedUrl(ctx);
    frame.src = ctx.frameUrl;
    if (className === 'trucustom__frame') frame.style.height = ctx.frameHeight + 'px';
    ctx.frame = frame;
    return frame;
  }

  function openModal(ctx) {
    if (ctx.modal) {
      // Reopening reuses the iframe, which still holds whichever design it was
      // last opened with. Re-point it first, or a customer who discarded a design
      // reopens onto that same design and finalizing there re-saves the old one
      // instead of starting fresh.
      syncFrame(ctx);
      ctx.modal.hidden = false;
      return;
    }

    var overlay = document.createElement('div');
    overlay.className = 'trucustom__overlay';

    var dialog = document.createElement('div');
    dialog.className = 'trucustom__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', 'Product customizer');

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'trucustom__close';
    close.setAttribute('aria-label', 'Close customizer');
    close.textContent = '×';
    close.addEventListener('click', function () { closeModal(ctx); });

    // Close lives in its own strip above the iframe rather than floating over
    // it — see .trucustom__bar. Overlaying this corner collided with the
    // customizer's own Done button.
    var bar = document.createElement('div');
    bar.className = 'trucustom__bar';
    bar.appendChild(close);

    dialog.appendChild(bar);
    dialog.appendChild(buildFrame(ctx, 'trucustom__dialog-frame'));
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    ctx.modal = overlay;

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeModal(ctx);
    });
  }

  function closeModal(ctx) {
    if (ctx.modal) ctx.modal.hidden = true;
  }

  // ── Saved-design confirmation ─────────────────────────────────────────────
  // Sits directly under the customize button. Without it, finishing a design is
  // indistinguishable from nothing having happened.

  function renderSaved(ctx, previewUrl) {
    hideNotice(ctx);
    if (!ctx.showPreview) return;

    clearSaved(ctx);

    var card = document.createElement('div');
    card.className = 'trucustom__saved';

    if (previewUrl) {
      var image = document.createElement('img');
      image.className = 'trucustom__saved-image';
      image.src = previewUrl;
      image.alt = 'Preview of your customized product';
      image.loading = 'lazy';
      // A broken preview must not leave a torn card — drop the image, keep the
      // confirmation, because the design itself is saved either way.
      image.addEventListener('error', function () {
        if (image.parentNode) image.parentNode.removeChild(image);
      });
      card.appendChild(image);
    }

    var body = document.createElement('div');
    body.className = 'trucustom__saved-body';

    var title = document.createElement('p');
    title.className = 'trucustom__saved-title';
    title.textContent = ctx.savedTitle;
    body.appendChild(title);

    var note = document.createElement('p');
    note.className = 'trucustom__saved-note';
    note.textContent = ctx.savedNote;
    body.appendChild(note);

    var actions = document.createElement('div');
    actions.className = 'trucustom__saved-actions';

    // Editing reopens the same design: the stored session token is passed back to
    // the customizer, so the customer resumes rather than starting over.
    var edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'trucustom__saved-edit';
    edit.textContent = ctx.editLabel;
    edit.addEventListener('click', function () {
      if (ctx.launch === 'inline') {
        if (ctx.frame) ctx.frame.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        openModal(ctx);
      }
    });
    actions.appendChild(edit);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'trucustom__saved-remove';
    remove.textContent = ctx.removeLabel;
    remove.addEventListener('click', function () { discardDesign(ctx); });
    actions.appendChild(remove);

    body.appendChild(actions);

    card.appendChild(body);
    ctx.root.appendChild(card);
    ctx.saved = card;
  }

  function clearSaved(ctx) {
    if (ctx.saved && ctx.saved.parentNode) ctx.saved.parentNode.removeChild(ctx.saved);
    ctx.saved = null;
  }

  // Detaches the finished design so the customer can buy the product as it is
  // after changing their mind.
  //
  // Deliberately does NOT re-gate. The gate exists to stop a half-finished design
  // reaching an order; discarding is the customer explicitly choosing no design at
  // all, and re-blocking add to cart would leave them stuck with no way forward
  // except customizing again. Note the consequence: a customer can now save and
  // then discard to buy an un-personalized item, so the gate no longer forces
  // personalization on a customizable product.
  //
  // Also deliberately does NOT delete the Design server-side. The record is
  // harmless once detached — nothing reaches the order — and a destructive call
  // from the storefront would need an authenticated path that doesn't exist here.
  // forgetToken is what stops the next Customize resuming the discarded design.
  function discardDesign(ctx) {
    clearProperties(ctx); // removes the hidden inputs AND the card — they're coupled
    forgetToken(ctx);
    ctx.forceFresh = true; // cleared by rememberToken once a new design exists
    // The token is gone, so the frame must stop showing that design. Matters
    // immediately in inline mode, where the frame is on screen the whole time,
    // and before the next open in modal mode.
    syncFrame(ctx);
    ungate(ctx); // no-op when nothing was gated; hides the notice either way
    // Back to square one on a customize-only product: the design the customer
    // just removed is the only thing that made this sellable.
    applyRequirement(ctx);
  }

  // ── postMessage bridge ────────────────────────────────────────────────────

  function listenForMessages(ctx) {
    window.addEventListener('message', function (event) {
      var data = event.data;
      if (!data || typeof data.type !== 'string') return;
      if (data.type.indexOf('trucustom:') !== 0) return;

      // Mandatory: the customizer posts with a '*' target origin, so without
      // this any embedded frame could forge a finalize and put an arbitrary
      // design token onto the order.
      //
      // Say so when we drop one. A mismatch here is invisible otherwise — the
      // customizer works, Done appears to do nothing, and nothing is logged.
      // The usual cause is the block's TruCustom URL not matching the origin the
      // iframe actually serves from (http vs https, or www vs apex).
      if (event.origin !== ctx.baseOrigin) {
        warn(
          'Ignored a "' + data.type + '" message from ' + event.origin +
          ' because the block is configured for ' + ctx.baseOrigin +
          '. Set the block\'s TruCustom URL to exactly that origin.'
        );
        return;
      }

      if (data.type === 'trucustom:designChange') {
        // Remember the token so a reopened customizer resumes the same design.
        //
        // Deliberately does NOT disable add-to-cart. Customizing is optional on
        // every product that offers it, so a design in progress is not a reason
        // to stop someone buying — and because a design in progress is the
        // NORMAL state while the customizer is open, gating here meant the
        // buttons spent most of their life disabled. A customer who adds to cart
        // mid-design simply buys the plain product, which is what they asked for
        // by pressing the button.
        //
        // The only moment TruCustom takes the buttons away is the round trip
        // after Done, while the finished design is being attached to the line —
        // see handleFinalize.
        if (data.design && data.design.session_token) rememberToken(ctx, data.design.session_token);
        return;
      }

      if (data.type === 'trucustom:finalize') handleFinalize(ctx, data.payload);
    });
  }

  function handleFinalize(ctx, payload) {
    var design = payload && payload.design;
    if (!design || !design.session_token) return warn('Finalize event had no design token; ignoring.');

    // Which Shopify variant (if any) charges for the customer's print type —
    // see ProductCustomizer#getSelectedPrintType and embed/show.html.erb's
    // onFinalize. Null is the common case (no print types, a free one, or one
    // not yet provisioned) and must never block the add-to-cart below.
    //
    // Captured now but NOT written onto ctx yet: attachDesign() below calls
    // applyProperties(), which calls clearProperties() to wipe any stale
    // design/surcharge before writing the new properties — and clearProperties
    // nulls ctx.surchargeVariantId as part of that. Setting it here would just
    // get clobbered a moment later, every single time, for every print type —
    // so it's written back AFTER attachDesign resolves instead.
    var surchargeVariantId = payload.surchargeVariantId || null;

    rememberToken(ctx, design.session_token);
    closeModal(ctx);
    // The one window where add-to-cart is genuinely unsafe: the design is
    // finished but its properties are not on the line yet, so a cart-add landing
    // right now would buy a plain product the customer believes is customized.
    // It lasts one request, and attachDesign lifts it either way below.
    gate(ctx);
    // Immediate feedback: pressing Done must visibly do something even though
    // the round trip below decides whether it worked. gate() shows nothing on
    // its own, so this is the only message during the hold.
    showNotice(ctx, ctx.savingLabel);

    attachDesign(ctx, design.session_token, design.preview_image_url)
      .then(function () {
        ctx.surchargeVariantId = surchargeVariantId;
      })
      .catch(function (error) {
        // The design is saved; what failed is attaching it to the cart line.
        // Re-enable the buttons anyway: leaving a storefront unable to sell is a
        // worse failure than an order that arrives without its design, and the
        // notice tells the customer what happened before they buy.
        warn('Could not load line item properties; the design is not on the line.', error);
        ungate(ctx);
        showNotice(ctx, 'We could not attach your design. Please refresh and try again before adding to your cart.');
      });
  }

  // Shared by finalize and the on-load restore.
  function attachDesign(ctx, token, previewFallback) {
    return fetchProperties(ctx, token).then(function (result) {
      applyProperties(ctx, result.properties);
      ungate(ctx);
      // ungate() only releases the plain disable-in-place hold. On a
      // customize-only product restored from a stored token, the requirement
      // swap (native button hidden, customizeTrigger standing in) was never
      // touched by ungate() — this is what puts Add to cart back. No-op for an
      // ordinary product.
      applyRequirement(ctx);
      renderSaved(ctx, result.previewUrl || previewFallback || null);
    });
  }

  // A customer who reloads the product page should not silently lose the
  // confirmation that their design exists. A finalized design returns 200 here;
  // a draft returns 422 and we simply stay gated.
  function restoreSavedDesign(ctx) {
    if (!ctx.sessionToken) return;

    attachDesign(ctx, ctx.sessionToken, null).catch(function () {
      // Draft, expired or unknown token — nothing to restore, stay gated.
      forgetToken(ctx);
    });
  }

  // ── Line item properties ──────────────────────────────────────────────────

  function fetchProperties(ctx, token) {
    var url = ctx.baseUrl + '/api/v1/widget/designs/' + encodeURIComponent(token) + '/line_item_properties';

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Properties returned HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        var properties = payload && payload.properties;
        if (!properties || !Object.keys(properties).length) throw new Error('Properties response was empty');
        // preview_url is display-only and served alongside the cart contract, so
        // the storefront never has to guess which property key holds the image.
        return { properties: properties, previewUrl: payload.preview_url || null };
      });
  }

  // Writes whatever the server sent by iterating it. Nothing here knows what the
  // keys are called — that is the point.
  function applyProperties(ctx, properties) {
    clearProperties(ctx);

    var keys = Object.keys(properties);

    // A fresh element per form: appending one node to several parents moves it
    // rather than copying it, which would leave every form but the last bare.
    ctx.forms.forEach(function (form) {
      keys.forEach(function (key) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + key + ']';
        input.value = properties[key];
        input.setAttribute('data-trucustom-prop', 'true');
        form.appendChild(input);
      });
    });

    ctx.properties = properties;
  }

  function clearProperties(ctx) {
    // The confirmation card and the injected properties represent the same fact —
    // "a finished design is attached to this line". They must never disagree, so
    // they are cleared together.
    clearSaved(ctx);
    ctx.properties = null;
    // A redone design may pick a different print type (and therefore a
    // different surcharge, or none) — this must not carry over stale.
    ctx.surchargeVariantId = null;

    ctx.forms.forEach(function (form) {
      form.querySelectorAll('[data-trucustom-prop]').forEach(function (node) {
        node.parentNode.removeChild(node);
      });
    });
  }

  // ── Variant changes ───────────────────────────────────────────────────────

  function watchVariantChanges(ctx) {
    // Watch every form we decorated. The set itself is fixed at boot — a variant
    // change swaps the value inside these forms, it does not create new ones —
    // so re-resolving here would only risk dropping a form whose value the theme
    // has not updated yet.
    ctx.forms.forEach(function (form) { watchOneForm(ctx, form); });
  }

  function watchOneForm(ctx, form) {
    form.addEventListener('change', function () {
      var input = form.querySelector('[name="id"]');
      var next = input && String(input.value || '').trim();
      if (!next || next === ctx.variantId) return;

      ctx.variantId = next;

      if (ctx.properties) {
        // The design records the variant it was made for, so letting a finalized
        // design ride onto a different variant is how a navy order gets printed
        // from a grey mockup. Re-confirm rather than ship a mismatch.
        // The design recorded the variant it was made for, so it cannot ride onto
        // a different one — but clearing it is enough for that. Disabling the
        // buttons as well would stop the customer buying the plain product they
        // just switched to.
        clearProperties(ctx);
        showNotice(ctx, 'You changed the options after customizing. Open the customizer again to put your design back on.');
      }

      // Unconditional, not just inside the branch above: a product that was
      // never customized yet (ctx.properties already null) still needs this,
      // because swapButtonsToCustomize's own relabel can get silently
      // overwritten by the theme re-rendering the button on this same variant
      // change. `silent` keeps the more specific message above in place when
      // it just ran, and stays quiet on the "nothing changed" case too.
      applyRequirement(ctx, { silent: true });

      syncFrame(ctx); // nothing finalized, nothing lost
    });
  }

  // ── Add to cart ───────────────────────────────────────────────────────────

  function interceptAddToCart(ctx) {
    if (!ctx.takeover) {
      // Theme owns the cart-add, so all we can do is watch the cart and record
      // the design once the line actually lands.
      ctx.forms.forEach(function (form) {
        form.addEventListener('submit', function () {
          if (ctx.properties) pollCartThenLock(ctx);
        });
      });
      return;
    }

    // Capture on the document, not the form: a listener on the form itself would
    // run after any handler the theme registered there first, and we need to be
    // able to stop the theme's own cart-add from also firing.
    document.addEventListener('submit', function (event) {
      // Any of this product's forms, not one nominated form — whichever button
      // the customer pressed is the one that has to carry the design.
      if (ctx.forms.indexOf(event.target) === -1) return;
      if (!ctx.properties) return; // gated — let the theme show its own validation

      event.preventDefault();
      event.stopImmediatePropagation();
      submitToCart(ctx, event.target);
    }, true);
  }

  // Serialize the form that was actually submitted. Serializing a nominated one
  // instead would post the wrong quantity whenever the theme's forms disagree.
  function submitToCart(ctx, form) {
    var request = ctx.surchargeVariantId ? addWithSurcharge(ctx, form) : addFormOnly(form);

    request
      .then(function (response) {
        if (response.ok) return response.json();
        return response.json().catch(function () { return {}; }).then(function (payload) {
          throw new Error(payload.description || payload.message || 'Cart returned HTTP ' + response.status);
        });
      })
      .then(function () {
        // Only now is the design genuinely in a cart. Locking earlier would slam
        // a one-way door on a customer whose cart-add then failed.
        return lockDesign(ctx);
      })
      .then(function () {
        forgetToken(ctx); // locked designs aren't editable; next time starts fresh
        finishAdd(ctx);
      })
      .catch(function (error) {
        warn('Add to cart failed.', error);
        showNotice(ctx, 'We could not add this to your cart. Please try again.');
      });
  }

  // ── Print-type surcharge line (takeover mode) ─────────────────────────────
  //
  // Print type pricing (see PrintingMethod#default_price) is charged by
  // adding a SECOND cart line alongside the product, priced at a Shopify
  // variant Integrations::ShopifyAdapter#ensure_surcharge_variant! provisions
  // — Shopify prices a cart line from its variant, never from a line item
  // property, so there is no way to fold the amount into the product's own
  // line.

  function addFormOnly(form) {
    return fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
  }

  // Adds the product AND its surcharge as one request (two lines, matching
  // quantity). Shopify validates a multi-item add atomically — if the
  // surcharge variant is stale (deleted/archived in Shopify since it was
  // provisioned), the WHOLE request fails and the product itself would never
  // reach the cart. Retrying with just the product on that specific failure
  // is what keeps a pricing gap from costing an actual sale — see this file's
  // own "degrade to nothing" rule at the top.
  function addWithSurcharge(ctx, form) {
    var mainItem = formToCartItem(form);
    return postCartItems([ mainItem, surchargeCartItem(ctx, mainItem.quantity) ])
      .then(function (response) {
        if (response.ok) return response;
        warn('Combined cart-add failed; retrying without the print-type surcharge.');
        return postCartItems([ mainItem ]);
      });
  }

  function postCartItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    });
  }

  function surchargeCartItem(ctx, quantity) {
    return {
      id: ctx.surchargeVariantId,
      quantity: quantity,
      properties: {
        // Hidden: how trucustom-cart.js finds this line's paired product line
        // to keep their quantities in sync — see its quantity-sync section.
        _tc_linked_design: ctx.sessionToken,
        // Visible: a cart holding more than one customized product would
        // otherwise show two identically-named "Screen Print" charges with
        // nothing distinguishing which product each belongs to.
        'For': ctx.productName || 'Customized product'
      }
    };
  }

  // Converts the submitted form into the shape /cart/add.js's multi-item JSON
  // body expects. Only id/quantity/properties[] and selling_plan (the one
  // other field Shopify's own cart item schema commonly carries) survive this
  // — an exotic theme field beyond those is dropped, same tradeoff a
  // multi-item JSON add always makes over a raw form POST.
  function formToCartItem(form) {
    var formData = new FormData(form);
    var item = { properties: {} };

    formData.forEach(function (value, key) {
      if (key === 'id') {
        item.id = value;
      } else if (key === 'quantity') {
        item.quantity = parseInt(value, 10) || 1;
      } else if (key === 'selling_plan') {
        item.selling_plan = value;
      } else {
        var match = key.match(/^properties\[(.+)\]$/);
        if (match) item.properties[match[1]] = value;
      }
    });

    if (!item.quantity) item.quantity = 1;
    return item;
  }

  function finishAdd(ctx) {
    if (ctx.afterAdd === 'reload') return window.location.reload();
    if (ctx.afterAdd !== 'none') return (window.location.href = '/cart');

    showNotice(ctx, 'Added to your cart.');
    try { document.dispatchEvent(new CustomEvent('trucustom:cart-updated')); } catch (e) {}
    notifyThemeCartUpdated(ctx);
  }

  // ── Theme cart-UI refresh ──────────────────────────────────────────────────
  //
  // Shopify's own product-form.js dispatches `shopify:cart:lines-update` after
  // a native add-to-cart — the signal a theme's cart drawer, cart icon badge
  // and header actions all listen for to refresh themselves (Shopify's
  // "Standard Events": https://cdn.shopify.com/storefront/standard-events.js,
  // the same URL most current themes, including this one, import as
  // `@shopify/events`). Two of this file's own cart-add paths never trigger
  // it, so the theme never learns the cart changed until the customer
  // happens to reload or navigate — the cart itself is correct the whole
  // time, only the on-page UI goes stale:
  //   - takeover mode intercepts the native submit before product-form.js
  //     ever runs (see interceptAddToCart), so its dispatch never happens.
  //   - non-takeover mode's surcharge line is added by addSurchargeFollowUp()
  //     below, a SEPARATE request fired after the theme already rendered its
  //     drawer for the main line — correct for the moment it ran, but
  //     nothing tells it to run again once the surcharge lands a moment
  //     later.
  //
  // Best-effort and silent: a theme that doesn't listen for the event, or
  // being offline, must never surface an error over what is purely a
  // cosmetic refresh — the add to cart itself already succeeded by the time
  // this runs.
  function notifyThemeCartUpdated(ctx) {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) {
        if (!cart) return;

        var lines = (cart.items || [])
          .filter(function (item) {
            return String(item.variant_id) === String(ctx.variantId) ||
              (ctx.surchargeVariantId && String(item.variant_id) === String(ctx.surchargeVariantId));
          })
          .map(function (item) {
            return { merchandiseId: item.variant_id, quantity: item.quantity };
          });

        dispatchCartLinesUpdate(cart, 'add', 'product', lines);
      })
      .catch(function (error) { warn('Could not refresh the theme cart UI.', error); });
  }

  // The event is built by hand rather than with Shopify's own
  // CartLinesUpdateEvent from standard-events.js, and this must NOT be
  // "simplified" back to a dynamic import. This is a classic (non-module)
  // script, and Shopify's theme asset pipeline rewrites a dynamic import in
  // those into a CommonJS require call, which doesn't exist in a browser —
  // so every refresh threw "require is not defined" on live stores while
  // working fine from unminified local source. The theme only reads the
  // properties set on the event itself (action, context, lines, promise),
  // and the promise resolves to {cart, detail}; the theme's own
  // pricing-tiers-cart-reconcile.js dispatches it the same way for the same
  // reason. Same copy in trucustom-cart.js — no shared runtime between the two files.
  function dispatchCartLinesUpdate(cart, action, context, lines) {
    var event = new Event('shopify:cart:lines-update', { bubbles: true, cancelable: false });
    event.action = action;
    event.context = context;
    event.lines = lines;
    event.promise = Promise.resolve({
      cart: cartSummary(cart),
      detail: { items: cart.items, source: 'trucustom', didError: false }
    });
    document.dispatchEvent(event);
  }

  // The Storefront-API-shaped cart summary the event's promise carries,
  // built from an Ajax API /cart.js response (prices there are in cents).
  function cartSummary(cart) {
    var currency = cart.currency || '';
    return {
      id: cart.token,
      totalQuantity: cart.item_count,
      cost: { totalAmount: { amount: (Number(cart.total_price) / 100).toFixed(2), currencyCode: currency } },
      lines: (cart.items || []).map(function (item) {
        return {
          id: item.key,
          quantity: item.quantity,
          cost: { totalAmount: { amount: (Number(item.final_line_price) / 100).toFixed(2), currencyCode: currency } }
        };
      }),
      discountCodes: (cart.discount_codes || []).map(function (d) { return { applicable: !!d.applicable, code: d.code }; })
    };
  }

  // The lock freezes canvas_data and preview_url so a cart line can't reference a
  // design that later changes. It is a durability improvement, not a checkout
  // requirement — a failure here must never block the purchase.
  //
  // Use cart/lock, NOT cart/add: this extension writes its own cart line, and
  // cart/add is the endpoint that also pushes one. It happens to skip the push
  // for Shopify today, but relying on that is how Swell ended up with duplicate
  // lines merged into double quantity (see CartController#lock).
  function lockDesign(ctx) {
    if (!ctx.sessionToken) return Promise.resolve();

    return fetch(ctx.baseUrl + '/api/v1/widget/cart/lock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ design_token: ctx.sessionToken })
    })
      .then(function (response) {
        if (!response.ok) warn('Design lock returned HTTP ' + response.status);
      })
      .catch(function (error) {
        warn('Design lock failed; the purchase continues regardless.', error);
      });
  }

  // Properties-only mode: confirm the line actually arrived before locking.
  // A full-page navigation can cut this short — accepted, there is no backstop.
  function pollCartThenLock(ctx) {
    var token = ctx.sessionToken;
    if (!token) return;

    var attempts = 0;

    function check() {
      attempts++;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (cart) {
          var matched = cart && findCartItemByToken(cart, token);
          if (matched) {
            // Non-takeover mode (see interceptAddToCart) never gets a chance
            // to fold the surcharge into the theme's own native cart-add, so
            // this is the fallback: now that the product is confirmed in the
            // cart, add it as its own follow-up request, at whatever quantity
            // the theme actually used — not re-read from the form, which may
            // have changed since the submit this is polling for.
            addSurchargeFollowUp(ctx, matched.quantity);
            lockDesign(ctx).then(function () { forgetToken(ctx); });
          } else if (attempts < 5) {
            window.setTimeout(check, 700);
          }
        })
        .catch(function () { /* page probably navigated */ });
    }

    window.setTimeout(check, 700);
  }

  function addSurchargeFollowUp(ctx, quantity) {
    if (!ctx.surchargeVariantId) return;

    // Best-effort: a failure here leaves a correctly-priced product sitting
    // in the cart with no surcharge line, never the other way around.
    postCartItems([ surchargeCartItem(ctx, quantity) ])
      .then(function (response) {
        // The theme's own drawer already rendered for the main line by now
        // (that's what pollCartThenLock was waiting on) — without this, the
        // surcharge sits correctly in the cart but stays invisible on screen
        // until the customer happens to reload or navigate. See
        // notifyThemeCartUpdated's own comment for the full picture.
        if (response.ok) notifyThemeCartUpdated(ctx);
      })
      .catch(function (error) {
        warn('Could not add the print-type surcharge line.', error);
      });
  }

  function findCartItemByToken(cart, token) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var properties = items[i].properties || {};
      var keys = Object.keys(properties);
      for (var j = 0; j < keys.length; j++) {
        if (String(properties[keys[j]]) === token) return items[i];
      }
    }
    return null;
  }

  function cartContainsToken(cart, token) {
    return !!findCartItemByToken(cart, token);
  }

  // ── Design token persistence ──────────────────────────────────────────────

  function tokenKey(ctx) {
    return TOKEN_STORE_PREFIX + ctx.shopifyProductId;
  }

  function readStoredToken(ctx) {
    try { return window.sessionStorage.getItem(tokenKey(ctx)) || null; } catch (e) { return null; }
  }

  function rememberToken(ctx, token) {
    ctx.sessionToken = token;
    // A design exists again, so the clean-start request is spent. Leaving it set
    // would make a later reopen throw away the draft now in progress.
    ctx.forceFresh = false;
    try { window.sessionStorage.setItem(tokenKey(ctx), token); } catch (e) {}
  }

  function forgetToken(ctx) {
    ctx.sessionToken = null;
    try { window.sessionStorage.removeItem(tokenKey(ctx)); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
